import { useEffect, useMemo, useState } from 'react';

// Client-side pagination for a list that is already fully loaded in memory.
// Owns both the current page and the page size, so a <Pagination> can render a
// "show N per page" selector. The page auto-resets to 1 whenever the list length
// or page size changes (e.g. a filter narrowed results), and is clamped so it
// never points past the last page.
export function usePaged(items, initialSize = 9) {
  const list = useMemo(() => items || [], [items]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const pageCount = Math.max(1, Math.ceil(list.length / pageSize));

  useEffect(() => {
    setPage((p) => Math.min(p, pageCount));
  }, [pageCount]);

  // Reset to the first page when the underlying set size or page size changes.
  useEffect(() => {
    setPage(1);
  }, [list.length, pageSize]);

  const start = (page - 1) * pageSize;
  const pageItems = list.slice(start, start + pageSize);

  return { pageItems, page, setPage, pageCount, pageSize, setPageSize };
}

// Server-side pagination state for lists fetched page by page from the backend.
// Owns page + pageSize and exposes skip/limit to feed the request. The component
// computes pageCount from the server `total` (hook ordering: call this BEFORE the
// query that consumes skip/limit). Pass a `resetKey` (e.g. the active filter or
// search) so changing it jumps back to page 1.
export function useServerPaged({ initialSize = 20, resetKey = '' } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);

  useEffect(() => {
    setPage(1);
  }, [pageSize, resetKey]);

  return { page, setPage, pageSize, setPageSize, skip: (page - 1) * pageSize, limit: pageSize };
}
